import { v4 as uuidv4 } from 'uuid';
import { parseSchemaCommand } from '../actions/commandParser.js';

const NODE_W = 240;
const NODE_H = 120;
const PAD_X  = 60;
const START_X = 40;
const START_Y = 180;

/**
 * Parse a .cmd script text into Vue Flow nodes + edges.
 * The trigger node (entry point) is kept from the existing flow so its id/position
 * is stable; all other nodes are rebuilt from the script content.
 *
 * @param {string} text         - raw .cmd file content
 * @param {string} scriptName   - e.g. 'boot'
 * @param {string} triggerId    - id of the existing trigger node to reuse
 * @returns {{ nodes: Array, edges: Array }}
 */
export function parseScript(text, scriptName, triggerId) {
    const lines = text
        .split('\n')
        .map(l => l.trim())
        .filter(l => l && !l.startsWith('#') && !l.startsWith('//'));

    const triggerNode = {
        id: triggerId ?? `trigger-${scriptName}`,
        type: 'trigger',
        position: { x: START_X, y: START_Y },
        data: { scriptName },
    };

    const actionNodes = [];
    for (const line of lines) {
        const node = parseLine(line);
        if (node) actionNodes.push(node);
    }

    // Layout: horizontal chain, nodes evenly spaced
    actionNodes.forEach((node, i) => {
        node.position = {
            x: START_X + (i + 1) * (NODE_W + PAD_X),
            y: START_Y,
        };
    });

    const nodes = [triggerNode, ...actionNodes];

    // Build sequence edges
    const edges = [];
    const chain = [triggerNode, ...actionNodes];
    for (let i = 0; i < chain.length - 1; i++) {
        edges.push({
            id: `e-${chain[i].id}-${chain[i + 1].id}`,
            source: chain[i].id,
            sourceHandle: 'output',
            target: chain[i + 1].id,
            targetHandle: 'input',
            animated: true,
            style: { stroke: '#94a3b8', strokeWidth: 2 },
        });
    }

    return { nodes, edges };
}

function uid(type) {
    return `${type}-${uuidv4().slice(0, 8)}`;
}

function parseLine(line) {
    // --- page <target>
    {
        const m = line.match(/^page\s+(\S+)$/i);
        if (m) return { id: uid('gotoPage'), type: 'gotoPage', position: { x: 0, y: 0 }, data: { pageTarget: m[1] } };
    }

    // --- clearpage <page|all>
    {
        const m = line.match(/^clearpage\s+(\S+)$/i);
        if (m) return { id: uid('clearPage'), type: 'clearPage', position: { x: 0, y: 0 }, data: { page: m[1] } };
    }

    // --- p{n}b{n}.{prop}={value}
    {
        const m = line.match(/^p(\d+)b(\d+)\.(\w+)=(.*)$/i);
        if (m) return {
            id: uid('setProperty'),
            type: 'setProperty',
            position: { x: 0, y: 0 },
            data: { pageId: parseInt(m[1]), objectId: parseInt(m[2]), property: m[3], value: m[4] },
        };
    }

    // --- backlight <json|state>
    {
        const m = line.match(/^backlight\s+(.+)$/i);
        if (m) {
            let state = true, brightness = 128;
            try {
                const json = JSON.parse(m[1]);
                state = json.state !== 0 && json.state !== false;
                brightness = json.brightness ?? brightness;
            } catch {
                state = m[1].trim() !== '0' && m[1].trim().toLowerCase() !== 'off';
            }
            return { id: uid('backlight'), type: 'backlight', position: { x: 0, y: 0 }, data: { state, brightness } };
        }
    }

    // --- moodlight <json>
    {
        const m = line.match(/^moodlight\s+(.+)$/i);
        if (m) {
            let state = true, r = 255, g = 0, b = 0;
            try {
                const json = JSON.parse(m[1]);
                state = json.state !== 0 && json.state !== false;
                r = json.r ?? r; g = json.g ?? g; b = json.b ?? b;
            } catch { /* keep defaults */ }
            return { id: uid('moodlight'), type: 'moodlight', position: { x: 0, y: 0 }, data: { state, r, g, b } };
        }
    }

    // --- output{n} <state>
    {
        const m = line.match(/^output(\d+)\s+(\S+)$/i);
        if (m) {
            const outputPin = parseInt(m[1]);
            let state = true;
            try { const json = JSON.parse(m[2]); state = json.state !== 0 && json.state !== false; }
            catch { state = m[2] !== '0' && m[2].toLowerCase() !== 'off'; }
            return { id: uid('output'), type: 'output', position: { x: 0, y: 0 }, data: { outputPin, state } };
        }
    }

    // --- delay <ms>
    {
        const m = line.match(/^delay\s+(\d+)$/i);
        if (m) return { id: uid('delay'), type: 'delay', position: { x: 0, y: 0 }, data: { duration: parseInt(m[1]) } };
    }

    // --- run <file>
    {
        const m = line.match(/^run\s+(\S+)$/i);
        if (m) return { id: uid('runScript'), type: 'runScript', position: { x: 0, y: 0 }, data: { filename: m[1] } };
    }

    // --- reboot / restart
    if (/^reboot$/i.test(line) || /^restart$/i.test(line)) {
        return { id: uid('reboot'), type: 'reboot', position: { x: 0, y: 0 }, data: {} };
    }

    // --- screenshot
    if (/^screenshot$/i.test(line)) {
        return { id: uid('screenshot'), type: 'screenshot', position: { x: 0, y: 0 }, data: {} };
    }

    // --- idle <state>
    {
        const m = line.match(/^idle\s+(\S+)$/i);
        if (m) return { id: uid('idle'), type: 'idle', position: { x: 0, y: 0 }, data: { idleState: m[1].toLowerCase() } };
    }

    // --- jsonl <json_payload>  (explicit command keyword)
    {
        const m = line.match(/^jsonl\s+(\{.+\})$/i);
        if (m) return { id: uid('jsonl'), type: 'jsonl', position: { x: 0, y: 0 }, data: { payload: m[1] } };
    }

    // --- bare jsonl line (starts with {) — treat as jsonl node
    if (line.startsWith('{')) {
        return { id: uid('jsonl'), type: 'jsonl', position: { x: 0, y: 0 }, data: { payload: line } };
    }

    // --- json array command
    if (line.startsWith('[')) return null;

    const parsed = parseSchemaCommand(line);
    if (parsed) {
        return { id: uid('command'), type: 'command', position: { x: 0, y: 0 }, data: parsed };
    }

    return null; // unknown command — silently skip
}
